import { chain } from 'lodash'
import groupBy from 'lodash/groupBy'
import meanBy from 'lodash/meanBy'
import sortBy from 'lodash/sortBy'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { order } from '../Utils/techniques'
import { RenovationCategory, TechniqueQuestions } from '../global'
import { useEligibilitySubmissionStore } from './eligibilitySubmission'
import { useQuestionStore } from './question'

export const useRenovationCategoryStore = defineStore('renovationCategory', () => {
    const questionStore = useQuestionStore()
    const eligibilitySubmissionStore = useEligibilitySubmissionStore()

    const orderOfCategories = computed(() =>
        questionStore.categories.map((category) => category.id)
    )

    const renovationCategories = computed<RenovationCategory[]>(() => {
        const techniqueQuestionsGroupedByRenovationCategory = groupBy<TechniqueQuestions>(
            questionStore.techniquesQuestions,
            'renovationCategory.id'
        )
        const techniquesByRenovationCategory = Object.values<TechniqueQuestions[]>(
            techniqueQuestionsGroupedByRenovationCategory
        ).filter(
            (techniques) =>
                !eligibilitySubmissionStore.isApartment ||
                techniques.some(({ openForApartments }) => openForApartments)
        )
        const items = techniquesByRenovationCategory.map((techniques) => {
            techniques = techniques.sort(order)

            const category = questionStore.categories.find(
                (item) => item.id === techniques[0].renovationCategoryId
            )

            return {
                ...category,
                techniques,
                questions: chain(techniques)
                    .flatMap('questions')
                    .filter({ type: 'technique_pre_qualification' })
                    .uniqBy('questionId')
                    .value(),
                averageImpact: meanBy(techniques, 'details.icon_economy'),
                averageCost: meanBy(techniques, 'details.icon_cost'),
                order: orderOfCategories.value.indexOf(category?.id || ''),
            } as RenovationCategory
        })

        return sortBy(items, 'order')
    })

    const selectedRenovationCategories = ref<string[]>([])
    const availableTechniqueIds = computed<string[]>(() =>
        renovationCategories.value
            .filter((category) => selectedRenovationCategories.value.includes(category.id))
            .flatMap((category) => category.techniques.map(({ uuid }) => uuid))
    )

    const renovationCategoriesToBeDisplayed = computed<RenovationCategory[]>(() =>
        renovationCategories.value.filter((renovationCategory) =>
            selectedRenovationCategories.value.includes(renovationCategory.id)
        )
    )

    function findRenovationCategoryById(id: string): RenovationCategory | undefined {
        return renovationCategories.value.find((item) => item.id === id)
    }

    function hasPreQualificationQuestions(id: string): boolean {
        return (findRenovationCategoryById(id)?.questions || []).length > 0
    }

    return {
        renovationCategories,
        selectedRenovationCategories,
        renovationCategoriesToBeDisplayed,
        findRenovationCategoryById,
        hasPreQualificationQuestions,
        availableTechniqueIds,
    }
})
